import React from "react";
import { ShoppingCart, Star } from "lucide-react";
import { money, API } from "@/lib/api";

const BRAND_QR = `${API}/postcard/brand-qr.png`;
const CURRENT_RUN_LABEL = new Date()
  .toLocaleDateString("en-US", { month: "short", year: "numeric" })
  .toUpperCase();

const TYPE_META = {
  premium: { size: '4"×9"', tone: "from-sam-crimson to-red-800" },
  standard: { size: '4"×6"', tone: "from-sam-navy to-sam-navy2" },
  basic: { size: '4"×3"', tone: "from-slate-600 to-slate-800" },
};

const LOGO = "https://customer-assets-jt897jd0.emergentagent.net/job_9dcac098-d714-42c7-8b47-2ce6640be931/artifacts/htqhr3eg_SAM%20logo%202%20NB%20cut.png";

// A–L, top→bottom, left→right (front A–E, back F–L). A = 4x9 premium.
const CELL_LABEL = {
  "f-hero": "A", "f-top-r": "B", "f-bot-l": "C", "f-bot-m": "D", "f-bot-r": "E",
  "b-top-1": "F", "b-top-2": "G", "b-top-3": "H", "b-top-4": "I",
  "b-bot-l": "J", "b-bot-m": "K", "b-bot-r": "L",
};

function FoundingBadge({ cell }) {
  return (
    <span className="absolute top-1.5 right-1.5 z-10 inline-flex items-center gap-1 rounded-full bg-amber-400 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-sam-navy shadow" title="This business started this card" data-testid={`founding-badge-${cell}`}>
      <Star className="h-2.5 w-2.5 fill-current" />Founding advertiser
    </span>
  );
}

function Slot({ slot, onSelect, selectable }) {
  if (!slot) return <div className="h-full w-full rounded-md bg-[#aec1de]/40 border border-dashed border-white/40" />;
  const meta = TYPE_META[slot.slot_type] || TYPE_META.basic;
  const label = CELL_LABEL[slot.cell];
  const clickable = selectable && slot.status === "available";
  const reserved = slot.status === "reserved";

  if (slot.status === "sold" && slot.ad_image_url) {
    return (
      <a href={slot.redeem_code ? `${API}/spot/${slot.redeem_code}/go?src=click` : "#"} className="relative block h-full w-full rounded-md overflow-hidden shadow-inner hover:ring-2 hover:ring-white" data-testid={`postcard-slot-${slot.cell}`} title={`Visit ${slot.business_name}`}>
        <img src={`${API}${slot.ad_image_url}`} alt={slot.business_name} className="h-full w-full object-cover" />
        <span className="absolute top-1.5 left-1.5 grid place-items-center h-5 w-5 rounded-full bg-white/90 text-sam-navy text-[11px] font-extrabold">{label}</span>
        {slot.founding && <FoundingBadge cell={slot.cell} />}
      </a>
    );
  }

  if (slot.status === "sold") {
    const listing = `${API}/spot/${slot.redeem_code}/go?src=click`;
    const qrSrc = `${API}/qr?data=${encodeURIComponent(`${API}/spot/${slot.redeem_code}/go`)}`;
    return (
      <a href={listing} className={`relative block h-full w-full rounded-md bg-gradient-to-br ${meta.tone} text-white p-2.5 flex flex-col justify-between overflow-hidden shadow-inner hover:ring-2 hover:ring-white`}
        data-testid={`postcard-slot-${slot.cell}`} title={`Visit ${slot.business_name}`}>
        <span className="absolute top-1.5 left-1.5 grid place-items-center h-5 w-5 rounded-full bg-white/90 text-sam-navy text-[11px] font-extrabold">{label}</span>
        {slot.founding && <FoundingBadge cell={slot.cell} />}
        <div className="pl-6">
          <span className="text-[9px] font-mono uppercase tracking-wide opacity-60">{meta.size}</span>
          <div className="font-display font-extrabold leading-tight text-[13px] sm:text-[15px] mt-0.5 line-clamp-2">{slot.business_name}</div>
          {slot.deal_headline && <div className="text-[10px] sm:text-[11px] mt-1 opacity-90 line-clamp-2 font-semibold">{slot.deal_headline}</div>}
        </div>
        <div className="flex items-end justify-between">
          <span className="text-[9px] font-bold uppercase opacity-70">Sold</span>
          <img src={qrSrc} alt={`QR to ${slot.business_name} listing`} className="h-9 w-9 sm:h-11 sm:w-11 rounded bg-white p-0.5" data-testid={`postcard-slot-qr-${slot.cell}`} />
        </div>
      </a>
    );
  }

  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={() => clickable && onSelect?.(slot)}
      data-testid={`postcard-slot-${slot.cell}`}
      className={`relative h-full w-full rounded-md p-2 flex flex-col items-center justify-center text-center transition-all
        ${reserved ? "bg-amber-100 border-2 border-sam-amber cursor-default" : "bg-[#aec1de] hover:bg-[#9db3d6] cursor-pointer hover:shadow-xl hover:ring-2 hover:ring-white"}`}
    >
      <span className="absolute top-1.5 left-1.5 grid place-items-center h-5 w-5 rounded-full bg-sam-crimson text-white text-[11px] font-extrabold">{label}</span>
      <span className="absolute top-1.5 right-2 text-[9px] font-mono uppercase tracking-wide text-white/70">{meta.size}</span>
      <span className="font-display font-extrabold text-white text-xl sm:text-2xl drop-shadow-sm leading-none">{meta.size}</span>
      {reserved ? (
        <span className="mt-1.5 text-[11px] font-bold uppercase text-sam-amber">Reserved</span>
      ) : (
        <>
          <span className="mt-1 font-display font-bold text-white text-[11px] sm:text-sm uppercase tracking-wide drop-shadow">Buy<br />This Spot</span>
          <span data-testid={`buy-spot-${slot.cell}`} className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-white bg-sam-crimson px-2.5 py-1 rounded-full shadow hover:bg-sam-crimson/90 transition-colors"><ShoppingCart className="h-3 w-3" />Buy · {money(slot.price)}</span>
        </>
      )}
    </button>
  );
}

function Band({ back, run }) {
  return (
    <div className="bg-white flex items-center gap-3 px-3 h-full overflow-hidden shadow">
      <img src={LOGO} alt="Saving America Money" className="h-8 sm:h-11 w-auto object-contain shrink-0" />
      <div className="leading-none shrink-0">
        <div className="font-display font-extrabold text-sm sm:text-lg"><span className="text-sam-crimson">Saving</span> <span className="text-sam-navy">America Money</span></div>
        <div className="font-display font-bold text-[10px] sm:text-xs"><span className="text-sam-crimson">local neighborhood</span> <span className="text-sam-navy">HERO's!</span></div>
      </div>
      <div className="flex-1" />
      {back ? (
        <div className="flex items-stretch gap-2 shrink-0" data-testid="postcard-mailing-panel">
          <div data-testid="postcard-run-month" className="bg-sam-navy text-white font-display font-extrabold text-[11px] sm:text-sm leading-none px-2 flex items-center justify-center rounded text-center uppercase">{CURRENT_RUN_LABEL}</div>
          <div className="flex items-center gap-2 border-2 border-dashed border-slate-300 rounded bg-slate-50/80 px-2 py-1">
            <div className="text-[9px] sm:text-[10px] text-slate-600 leading-tight uppercase font-semibold whitespace-nowrap">Local<br />Postal Customer<br /><span className="tracking-[0.2em] text-slate-400">ECRWSS</span></div>
            <div className="border border-slate-400 text-[7px] sm:text-[8px] text-slate-600 leading-tight px-1.5 py-1 text-center bg-white whitespace-nowrap" data-testid="postcard-indicia">PRESORTED STD<br /><b>U.S. POSTAGE PAID</b><br />PERMIT #____</div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-right text-sam-navy font-display font-bold text-[10px] sm:text-sm leading-tight uppercase">Scan to visit<br />Saving America Money</div>
          <img src={BRAND_QR} alt="Scan to visit Saving America Money" data-testid="postcard-brand-qr" className="h-9 w-9 sm:h-12 sm:w-12 rounded bg-white p-0.5 object-contain" />
        </div>
      )}
    </div>
  );
}

export function PostcardSide({ slots, side, run, onSelect, selectable }) {
  const cell = (c) => slots.find((s) => s.cell === c);
  const isFront = side === "front";
  return (
    <div className="rounded-xl bg-[#e9eef5] p-2 sm:p-3 shadow-2xl overflow-hidden" data-testid={`postcard-${side}`}>
      <div className="w-full flex flex-col gap-2" style={{ aspectRatio: "12 / 9" }}>
        {/* TOP — 4 in tall */}
        <div className="flex gap-2" style={{ flex: "0 0 44.4%" }}>
          {isFront ? (
            <>
              <div style={{ flex: "0 0 75%" }}><Slot slot={cell("f-hero")} onSelect={onSelect} selectable={selectable} /></div>
              <div className="flex-1"><Slot slot={cell("f-top-r")} onSelect={onSelect} selectable={selectable} /></div>
            </>
          ) : (
            ["b-top-1", "b-top-2", "b-top-3", "b-top-4"].map((c) => (
              <div key={c} className="flex-1"><Slot slot={cell(c)} onSelect={onSelect} selectable={selectable} /></div>
            ))
          )}
        </div>
        {/* BAND — 1 in gap for logo, address & postage */}
        <div style={{ flex: "0 0 11.2%" }} className="min-h-0">
          <Band back={!isFront} run={run} />
        </div>
        {/* BOTTOM — 4 in tall: 4x6 (50%) + two 4x3 (25% each) */}
        <div className="flex gap-2" style={{ flex: "0 0 44.4%" }}>
          <div style={{ flex: "0 0 50%" }}><Slot slot={cell(isFront ? "f-bot-l" : "b-bot-l")} onSelect={onSelect} selectable={selectable} /></div>
          <div className="flex-1"><Slot slot={cell(isFront ? "f-bot-m" : "b-bot-m")} onSelect={onSelect} selectable={selectable} /></div>
          <div className="flex-1"><Slot slot={cell(isFront ? "f-bot-r" : "b-bot-r")} onSelect={onSelect} selectable={selectable} /></div>
        </div>
      </div>
    </div>
  );
}

export default function PostcardCanvas({ slots: rawSlots = [], onSelect, selectable = false, run }) {
  const fid = run?.founding_business_id;
  const slots = fid ? rawSlots.map((s) => (s.status === "sold" && s.business_id === fid ? { ...s, founding: true } : s)) : rawSlots;
  return (
    <div data-testid="postcard-canvas" className="space-y-6">
      <div>
        <div className="mb-2 flex items-center justify-between px-1">
          <span className="text-xs font-mono uppercase tracking-widest text-sam-slate">Front · 9 × 12 in landscape</span>
          <span className="text-xs font-mono text-sam-slate">10,000 mailed</span>
        </div>
        <PostcardSide slots={slots} side="front" run={run} onSelect={onSelect} selectable={selectable} />
      </div>
      <div>
        <div className="mb-2 px-1"><span className="text-xs font-mono uppercase tracking-widest text-sam-slate">Back</span></div>
        <PostcardSide slots={slots} side="back" run={run} onSelect={onSelect} selectable={selectable} />
      </div>
    </div>
  );
}
